import { apiClient, ApiError } from './client';
import type { components, paths } from './schema';
export type Opportunity = components['schemas']['OpportunityRow'];
export type OpportunityInput = components['schemas']['OpportunityInput'];
export type OpportunityDetail = components['schemas']['OpportunityDetail'];
export type OpportunityCustomer = components['schemas']['OpportunityCustomer'];
export type OpportunityNextStep = components['schemas']['OpportunityNextStep'];
export type NextStepKind = OpportunityNextStep['kind'];
export type NextStepTarget = OpportunityNextStep['target'];
export type OpportunityStatus = NonNullable<Opportunity['status']>;
export type PipelineFilters = NonNullable<paths['/api/pipeline']['get']['parameters']['query']>;
/** What a person may name when a call or a move leaves the deal needing something else next. */
export interface NextActionChange {
  readonly nextAction?: string;
  /** An ISO instant. */
  readonly nextActionDue?: string;
}
/** A contact being logged: what was said, and optionally what happens next. */
export interface ContactLog extends NextActionChange {
  readonly note?: string;
}
/**
 * What a move can carry besides the new stage.
 *
 * `reason` is why the deal is going nowhere: the server refuses a move into Lost or No decision
 * without one.
 */
export interface MoveDetails extends NextActionChange {
  readonly reason?: string;
}
export type BookedConsultation = components['schemas']['OpportunityConsultationBooked'];
/** What a person chooses when booking the sales visit; the deal's version is added on send. */
export type ConsultationBooking = Omit<
  components['schemas']['OpportunityBookConsultation'],
  'expectedVersion'
>;
function result<T>(r: { data?: T; response: Response; error?: unknown }): T {
  if (!r.response.ok || r.data === undefined) {
    const detail =
      typeof r.error === 'object' && r.error !== null && 'detail' in r.error
        ? String(r.error.detail)
        : 'The pipeline request failed';
    throw new ApiError(r.response.status, detail);
  }
  return r.data;
}
export async function searchPipeline(query: PipelineFilters, attention = false) {
  return attention
    ? result(
        await apiClient.GET('/api/pipeline/needs-attention', {
          params: { query: { page: query.page ?? 0, size: query.size ?? 100 } },
        }),
      )
    : result(await apiClient.GET('/api/pipeline', { params: { query } }));
}
export async function attentionCount() {
  return result(await apiClient.GET('/api/pipeline/needs-attention/count'));
}
export async function pipelinePeople() {
  return result(await apiClient.GET('/api/pipeline/people'));
}
export async function readOpportunity(id: string) {
  return result(await apiClient.GET('/api/pipeline/{id}', { params: { path: { id } } }));
}
export async function saveOpportunity(fields: OpportunityInput, current?: Opportunity) {
  return current
    ? result(
        await apiClient.PUT('/api/pipeline/{id}', {
          params: { path: { id: current.id } },
          body: { expectedVersion: current.version, fields },
        }),
      )
    : result(await apiClient.POST('/api/pipeline', { body: fields }));
}
/**
 * Moves a deal to one of its legal next states.
 *
 * Leaving the next action out is how the stage default applies, so nothing is sent unless the
 * person named one. Ending a deal in Lost or No decision needs a `reason`.
 */
export async function transitionOpportunity(
  row: Opportunity,
  status: OpportunityStatus,
  next: MoveDetails = {},
) {
  return result(
    await apiClient.POST('/api/pipeline/{id}/transitions', {
      params: { path: { id: row.id } },
      body: { expectedVersion: row.version, status, ...next },
    }),
  );
}
/**
 * Books the sales visit and walks the deal to Appointment scheduled in one call.
 *
 * Only a deal that is New, Contacted or Qualified can be booked.
 */
export async function bookConsultation(row: Opportunity, booking: ConsultationBooking) {
  return result(
    await apiClient.POST('/api/pipeline/{id}/book-consultation', {
      params: { path: { id: row.id } },
      body: { expectedVersion: row.version, ...booking },
    }),
  );
}
/** Records that the customer was just contacted; a deal still at NEW becomes Contacted. */
export async function logContact(row: Opportunity, contact: ContactLog = {}) {
  return result(
    await apiClient.POST('/api/pipeline/{id}/log-contact', {
      params: { path: { id: row.id } },
      body: { expectedVersion: row.version, ...contact },
    }),
  );
}
export async function resumeOpportunity(row: Opportunity, reason: string) {
  return result(
    await apiClient.POST('/api/pipeline/{id}/resume', {
      params: { path: { id: row.id } },
      body: { expectedVersion: row.version, reason },
    }),
  );
}
